import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  LocalizePropType,
  preLoginWithEmail,
  withTextLocalizer,
} from 'getaway-data-layer';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import EmailLoginView from './EmailLoginView';
import { ForgotPasswordModal, ForgotPasswordSchema } from '../../../validation';
import { navigateTo } from '../../../utils';
import { useQuery } from '../../../hooks/useQuery';

interface Values {
  email: string;
}

type Props = {
  localize: LocalizePropType;
};

const EmailLoginContainer = ({ localize }: Props) => {
  const history = useHistory();
  const query = useQuery();
  const dispatch = useDispatch();
  const turnstileRef = useRef<HTMLDivElement>(null);
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(
    null
  );
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const email = query.get('email');

  const cleanupTurnstile = () => {
    if (turnstileWidgetId && window.turnstile) {
      try {
        // @ts-ignore
        window.turnstile.remove(turnstileWidgetId);
      } catch (e) {
        console.error('Error removing turnstile widget:', e);
      }
    }
    setTurnstileWidgetId(null);
    setTurnstileToken(null);
  };

  const turnstileCheck = () => {
    // Clean up any existing widget before creating a new one
    cleanupTurnstile();

    // Create new widget
    const widgetId = window.turnstile.render(turnstileRef.current, {
      sitekey: process.env.VITE_TURNSTILE_SITE_KEY,
      callback: (token: string) => {
        setTurnstileToken(token);
      },
      theme: 'light',
      retry: 'never',
    });

    setTurnstileWidgetId(widgetId);
  };

  const onSubmit = ({ email }: Values) => {
    turnstileCheck();
  };

  const preLogin = ({ email }: Values) => {
    const payload = {
      email: email.trim(),
      captchaToken: turnstileToken,
    };
    dispatch(
      preLoginWithEmail(
        payload,
        () => {
          navigateTo(
            history,
            '/confirm-email?email=' + encodeURIComponent(email.trim())
          );
          cleanupTurnstile();
        },
        () => {
          cleanupTurnstile();
        }
      )
    );
  };

  const emailLoginFormik = useFormik({
    initialValues: {
      ...ForgotPasswordModal,
      email: email || ForgotPasswordModal.email,
    },
    validationSchema: ForgotPasswordSchema(localize),
    onSubmit,
  });

  const onClose = () => {
    navigateTo(history, '/login');
  };

  const onPressSignup = () => {
    navigateTo(history, '/signup');
  };

  useEffect(() => {
    if (turnstileToken && emailLoginFormik.values) {
      preLogin(emailLoginFormik.values);
    }
  }, [turnstileToken]);

  useEffect(() => {
    if (email) {
      emailLoginFormik.submitForm();
    }
  }, []);

  return (
    <EmailLoginView
      localize={localize}
      formik={emailLoginFormik}
      turnstileRef={turnstileRef}
      onClose={onClose}
      onPressSignup={onPressSignup}
      isTurnstileLoading={!!turnstileWidgetId}
    />
  );
};

export default withTextLocalizer(EmailLoginContainer);
